import { createContext, useContext, useMemo, ReactNode } from "react";
import { useUserRole } from "@/hooks/useUserRole";
import { Database } from "@/integrations/supabase/types";

type AppRole = Database["public"]["Enums"]["app_role"];

interface Workspace {
  id: string;
  name: string;
  slug: string;
  owner_user_id: string;
  max_seats: number;
  used_seats: number;
  subscription_status: string;
  subscription_tier: string;
  enterprise_features_enabled?: boolean;
  campaigns_managed_by_admin?: boolean;
  billing_email?: string | null;
  trial_started_at?: string | null;
  trial_ends_at?: string | null;
  is_international?: boolean;
  support_chatbot_enabled?: boolean;
  partner_id?: string | null;
  settings?: any;
}

interface WorkspaceContextType {
  workspace: Workspace | null;
  role: AppRole | null;
  loading: boolean;
  refetch: () => Promise<void>;
  isSuperAdmin: boolean;
  isWorkspaceOwner: boolean;
  isWorkspaceAdmin: boolean;
  isMember: boolean;
  isWorkspaceAdminOrOwner: boolean;
  // Super admin impersonation
  isImpersonating: boolean;
  actualRole: AppRole | null;
  isActuallySuperAdmin: boolean; // True even when impersonating
  isActuallyPartnerAdmin: boolean;
  actualPartnerId: string | null;
  impersonateWorkspace: (workspaceId: string) => Promise<boolean>;
  impersonateWorkspaceAsPartner: (workspaceId: string) => Promise<boolean>;
  stopImpersonating: () => Promise<void>;
  isActuallySubAdmin: boolean;
  subAdminWorkspaceIds: string[];
  impersonateWorkspaceAsSubAdmin: (workspaceId: string) => Promise<boolean>;
}

export const WorkspaceContext = createContext<WorkspaceContextType | undefined>(undefined);

export const WorkspaceProvider = ({ children }: { children: ReactNode }) => {
  const userRoleData = useUserRole();

  // Memoize the context value keyed on primitive fields. Without this, every
  // render of useUserRole returns a new object reference and forces every
  // useWorkspace() consumer to re-render — even when nothing relevant changed.
  const value = useMemo(
    () => userRoleData,
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [
      userRoleData.workspace?.id,
      userRoleData.workspace?.subscription_status,
      userRoleData.workspace?.subscription_tier,
      userRoleData.workspace?.used_seats,
      userRoleData.workspace?.max_seats,
      (userRoleData.workspace as any)?.support_chatbot_enabled,
      userRoleData.role,
      userRoleData.loading,
      userRoleData.isImpersonating,
      userRoleData.actualRole,
      userRoleData.isActuallySubAdmin,
      userRoleData.subAdminWorkspaceIds.join(','),
    ]
  );

  return (
    <WorkspaceContext.Provider value={value}>
      {children}
    </WorkspaceContext.Provider>
  );
};

export const useWorkspace = () => {
  const context = useContext(WorkspaceContext);
  if (context === undefined) {
    throw new Error("useWorkspace must be used within a WorkspaceProvider");
  }
  return context;
};
